import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-gray-800 rounded-lg shadow-xl p-8">
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="text-blue-400 hover:text-blue-300 mb-4"
            >
              ← Back
            </button>
            <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-gray-400">Last Updated: January 24, 2025</p>
          </div>

          <div className="prose prose-invert max-w-none">
            <div className="text-gray-300 space-y-6">
              
              <section>
                <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
                <p>
                  TriviaPlug ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Platform.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
                
                <h3 className="text-xl font-semibold text-white mt-4 mb-2">2.1 Personal Information</h3>
                <p>We collect information that you provide directly, including:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Name, email address, and account credentials</li>
                  <li>Payment information (processed securely through Stripe)</li>
                  <li>Profile information and preferences</li>
                  <li>Game content and marketplace submissions</li>
                  <li>Support communications and feedback</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">2.2 Automatically Collected Information</h3>
                <p>We automatically collect certain information when you use the Platform:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Device information (IP address, browser type, operating system)</li>
                  <li>Usage data (pages visited, features used, time spent)</li>
                  <li>Game session data and performance metrics</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">2.3 Payment Information</h3>
                <p>
                  Payment processing is handled by Stripe. TriviaPlug does not store your complete credit card information. We only store payment tokens and transaction records necessary for our services.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">3. How We Use Your Information</h2>
                <p>We use the collected information to:</p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Provide, maintain, and improve our Platform services</li>
                  <li>Process transactions and manage accounts</li>
                  <li>Facilitate marketplace sales and revenue sharing</li>
                  <li>Send administrative information and updates</li>
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Detect, prevent, and address technical issues and security threats</li>
                  <li>Monitor Platform usage and analytics</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">4. How We Share Your Information</h2>
                
                <h3 className="text-xl font-semibold text-white mt-4 mb-2">4.1 With Service Providers</h3>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Stripe:</strong> Payment processing and creator payouts via Stripe Connect</li>
                  <li><strong>Firebase/Google Cloud:</strong> Hosting, database, and authentication services</li>
                  <li>Other third-party services necessary for Platform operation</li>
                </ul>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">4.2 Marketplace Information</h3>
                <p>
                  When you submit games to the marketplace, your game content, creator name, and profile information may be visible to other users who browse or purchase games.
                </p>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">4.3 Legal Requirements</h3>
                <p>
                  We may disclose your information if required by law, court order, or governmental authority, or to protect the rights, property, or safety of TriviaPlug, our users, or others.
                </p>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">4.4 Business Transfers</h3>
                <p>
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">5. Data Security</h2>
                <p>
                  We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2 mt-2">
                  <li>Encryption of data in transit (SSL/TLS)</li>
                  <li>Secure authentication and authorization systems</li>
                  <li>Regular security audits and monitoring</li>
                  <li>Firestore security rules and access controls</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">6. Your Privacy Rights</h2>
                
                <h3 className="text-xl font-semibold text-white mt-4 mb-2">6.1 Access and Correction</h3>
                <p>
                  You can access and update your account information through the Platform's account settings page.
                </p>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">6.2 Data Deletion</h3>
                <p>
                  You may request deletion of your account and personal data by contacting us. Note that we may retain certain information as required by law or for legitimate business purposes.
                </p>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">6.3 Opt-Out</h3>
                <p>
                  You can opt out of marketing communications by using the unsubscribe link in emails or adjusting your account preferences.
                </p>

                <h3 className="text-xl font-semibold text-white mt-4 mb-2">6.4 GDPR Rights (EU Users)</h3>
                <p>
                  If you are in the European Union, you have additional rights under GDPR:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Right to access your personal data</li>
                  <li>Right to rectification of inaccurate data</li>
                  <li>Right to erasure ("right to be forgotten")</li>
                  <li>Right to restrict processing</li>
                  <li>Right to data portability</li>
                  <li>Right to object to processing</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">7. Cookies and Tracking</h2>
                <p>
                  We use cookies and similar technologies to enhance your experience, analyze Platform usage, and assist with security. You can control cookies through your browser settings, though this may affect Platform functionality.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">8. Children's Privacy</h2>
                <p>
                  The Platform is not intended for users under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us immediately.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">9. International Data Transfers</h2>
                <p>
                  Your information may be transferred to and processed in the United States or other countries where our service providers operate. By using the Platform, you consent to such transfers.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">10. Data Retention</h2>
                <p>
                  We retain your personal information for as long as necessary to provide our services and comply with legal obligations. When you delete your account, we will delete or anonymize your personal data, except where retention is required by law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">11. California Privacy Rights (CCPA)</h2>
                <p>
                  California residents have the right to:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li>Know what personal information is collected about them</li>
                  <li>Know whether personal information is sold or disclosed</li>
                  <li>Opt-out of the sale of personal information (we do not sell personal information)</li>
                  <li>Access and delete their personal information</li>
                  <li>Non-discrimination for exercising privacy rights</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">12. Third-Party Services</h2>
                <p>
                  The Platform integrates with third-party services (Stripe, Firebase, etc.) that have their own privacy policies. We encourage you to review their privacy policies:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-2">
                  <li><strong>Stripe:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">stripe.com/privacy</a></li>
                  <li><strong>Firebase/Google:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">policies.google.com/privacy</a></li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">13. Changes to This Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of material changes by email or through a Platform notification. Your continued use of the Platform after changes constitutes acceptance of the updated Privacy Policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-bold text-white mb-4">14. Contact Us</h2>
                <p>
                  If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
                </p>
                <ul className="list-none ml-4 space-y-2">
                  <li>Email: support@triviaplug.com</li>
                  <li>Support Tickets: Available through the Help Desk page</li>
                </ul>
                <p className="mt-4">
                  For GDPR-related requests, please include "GDPR Request" in your subject line.
                </p>
              </section>

            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-700">
            <p className="text-gray-400 text-sm">
              By using TriviaPlug, you acknowledge that you have read and understood this Privacy Policy.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;



